import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { api } from "../services/api";

const QuizMCQsLayer = () => {
  const navigate = useNavigate();

  // State for categories and subcategories
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Quiz management state
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSubcategory, setSelectedSubcategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editQuizIndex, setEditQuizIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New quiz form
  const [newQuiz, setNewQuiz] = useState({
    id: null,
    title: "",
    description: "",
    categoryId: "",
    questionCount: 10,
    timeLimit: 15,
    passingScore: 70,
    isActive: true,
    dateCreated: new Date().toISOString().split("T")[0],
  });

  // Quizzes from API
  const [quizzes, setQuizzes] = useState([]);

  // Filtered quizzes based on search criteria
  const [filteredQuizzes, setFilteredQuizzes] = useState([]);

  // State for quiz questions
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editQuestionIndex, setEditQuestionIndex] = useState(null);
  const [newQuestion, setNewQuestion] = useState({
    id: null,
    questionText: "",
    options: ["", "", "", ""],
    correctAnswer: 0,
    explanation: "",
    categoryId: "",
    isActive: true,
  });

  // State for available practice questions
  const [availablePracticeQuestions, setAvailablePracticeQuestions] = useState([]);
  const [showAvailableQuestions, setShowAvailableQuestions] = useState(false);
  const [selectedPracticeQuestions, setSelectedPracticeQuestions] = useState([]);
  const [loadingPracticeQuestions, setLoadingPracticeQuestions] = useState(false);
  const [practiceQuestionSearchTerm, setPracticeQuestionSearchTerm] = useState("");

  // CSV import related state and refs
  const csvFileInputRef = useRef(null);
  const [csvError, setCsvError] = useState(null);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.categories.getAll();
        if (response.success) {
          setCategories(response.data);
        } else {
          console.error("Failed to fetch categories:", response.message);
          setError("Failed to fetch categories");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        setError("Error fetching categories");
      }
    };

    const fetchQuizzes = async () => {
      try {
        setLoading(true);
        const response = await api.quizzes.getAll();
        if (response.success) {
          setQuizzes(response.data);
        } else {
          console.error("Failed to fetch quizzes:", response.message);
          setError("Failed to fetch quizzes");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching quizzes:", error);
        setError("Error fetching quizzes");
        setLoading(false);
      }
    };

    fetchCategories();
    fetchQuizzes();
  }, []);

  // Fetch subcategories when the selected category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (selectedCategory !== "All") {
        try {
          const categoryId = categories.find(
            (cat) => cat.name === selectedCategory
          )?.id;
          if (categoryId) {
            const response = await api.categories.getSubcategories(categoryId);
            if (response.success) {
              setSubcategories(response.data);
            } else {
              console.error("Failed to fetch subcategories:", response.message);
              setSubcategories([]);
            }
          } else {
            setSubcategories([]);
          }
        } catch (error) {
          console.error("Error fetching subcategories:", error);
          setSubcategories([]);
        }
      } else {
        setSubcategories([]);
      }
    };

    fetchSubcategories();
  }, [selectedCategory, categories]);

  // Handle category change
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSelectedSubcategory("All");
  };

  // Handle subcategory change
  const handleSubcategoryChange = (e) => {
    setSelectedSubcategory(e.target.value);
  };

  // Filter quizzes based on criteria
  useEffect(() => {
    let filtered = [...quizzes];

    // Apply category filter
    if (selectedCategory !== "All") {
      const categoryId = categories.find(
        (cat) => cat.name === selectedCategory
      )?.id;
      filtered = filtered.filter((quiz) => quiz.categoryId === categoryId);
    }

    // Apply subcategory filter if searching practices not quizzes
    if (selectedSubcategory !== "All") {
      const subcategoryId = subcategories.find(
        (subcat) => subcat.name === selectedSubcategory
      )?.id;
      filtered = filtered.filter(
        (quiz) => quiz.practiceQuestions?.some(q => q.subcategoryId === subcategoryId)
      );
    }

    // Apply search term filter
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (quiz) =>
          quiz.title.toLowerCase().includes(term) ||
          quiz.description.toLowerCase().includes(term)
      );
    }

    setFilteredQuizzes(filtered);
  }, [
    quizzes,
    selectedCategory,
    selectedSubcategory,
    searchTerm,
    categories,
    subcategories,
  ]);

  // Add new quiz
  const addQuiz = () => {
    setEditQuizIndex(null);
    const defaultCategoryId = categories.length > 0 ? categories[0].id : "";

    setNewQuiz({
      id: null,
      title: "",
      description: "",
      categoryId: defaultCategoryId,
      questionCount: 10,
      timeLimit: 15,
      passingScore: 70,
      isActive: true,
      dateCreated: new Date().toISOString().split("T")[0],
    });
    setShowAddModal(true);
  };

  // Edit existing quiz
  const editQuiz = (index) => {
    setEditQuizIndex(index);
    const quizToEdit = filteredQuizzes[index];

    // Make sure the categoryId is a string
    setNewQuiz({
      ...quizToEdit,
      categoryId: quizToEdit.categoryId?.toString() || "",
    });

    setShowAddModal(true);
  };

  // Delete quiz
  const deleteQuiz = async (id) => {
    if (window.confirm("Are you sure you want to delete this quiz?")) {
      try {
        const response = await api.quizzes.delete(id);
        if (response.success) {
          setQuizzes(quizzes.filter((q) => q.id !== id));
          toast.success("Quiz deleted successfully");
        } else {
          toast.error("Failed to delete quiz: " + response.message);
        }
      } catch (error) {
        console.error("Error deleting quiz:", error);
        toast.error("Error deleting quiz");
      }
    }
  };

  // Toggle quiz active status
  const toggleQuizStatus = async (id, currentStatus) => {
    try {
      const response = await api.quizzes.update(id, {
        isActive: !currentStatus,
      });
      if (response.success) {
        const updatedQuizzes = quizzes.map((quiz) =>
          quiz.id === id ? { ...quiz, isActive: !quiz.isActive } : quiz
        );
        setQuizzes(updatedQuizzes);
        toast.success("Quiz status updated successfully");
      } else {
        toast.error("Failed to update quiz status: " + response.message);
      }
    } catch (error) {
      console.error("Error updating quiz status:", error);
      toast.error("Error updating quiz status");
    }
  };

  // Handle form input changes
  const handleQuizChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "isActive") {
      setNewQuiz({ ...newQuiz, [name]: checked });
    } else if (["questionCount", "timeLimit", "passingScore"].includes(name)) {
      setNewQuiz({ ...newQuiz, [name]: parseInt(value, 10) || 0 });
    } else {
      setNewQuiz({ ...newQuiz, [name]: value });
    }
  };

  // Save quiz
  const saveQuiz = async () => {
    // Form validation
    if (!newQuiz.title.trim()) {
      toast.error("Quiz title is required");
      return;
    }

    if (newQuiz.questionCount < 1) {
      toast.error("Quiz must have at least 1 question");
      return;
    }

    if (newQuiz.passingScore < 1 || newQuiz.passingScore > 100) {
      toast.error("Passing score must be between 1 and 100");
      return;
    }

    try {
      let response;
      if (editQuizIndex !== null) {
        // Update existing quiz
        response = await api.quizzes.update(newQuiz.id, newQuiz);
        if (response.success) {
          const updatedQuizzes = quizzes.map((quiz) =>
            quiz.id === newQuiz.id ? response.data : quiz
          );
          setQuizzes(updatedQuizzes);
          toast.success("Quiz updated successfully");
        } else {
          toast.error("Failed to update quiz: " + response.message);
        }
      } else {
        // Add new quiz
        response = await api.quizzes.create(newQuiz);
        if (response.success) {
          setQuizzes([...quizzes, response.data]);
          toast.success("Quiz created successfully");
        } else {
          toast.error("Failed to create quiz: " + response.message);
        }
      }
      setShowAddModal(false);
    } catch (error) {
      console.error("Error saving quiz:", error);
      toast.error("Error saving quiz");
    }
  };

  // Duplicate quiz
  const duplicateQuiz = async (quiz) => {
    try {
      const duplicatedQuizData = {
        ...quiz,
        title: `Copy of ${quiz.title}`,
        id: undefined, // Remove the id so the API creates a new one
      };

      const response = await api.quizzes.create(duplicatedQuizData);
      if (response.success) {
        setQuizzes([...quizzes, response.data]);
        toast.success("Quiz duplicated successfully");
      } else {
        toast.error("Failed to duplicate quiz: " + response.message);
      }
    } catch (error) {
      console.error("Error duplicating quiz:", error);
      toast.error("Error duplicating quiz");
    }
  };

  // Manage quiz questions
  const manageQuestionsInline = async (quiz) => {
    setActiveQuiz(quiz);
    setLoading(true);

    try {
      const response = await api.quizzes.getQuizQuestions(quiz.id);
      if (response.success) {
        // Process the response data structure - each item has a question property containing the actual question data
        const formattedQuestions = response.data.map((item) => {
          // Handle both direct question objects and nested question objects
          const question = item.question || item;
          return {
            ...question,
            id: question.id,
            // Ensure categoryId is present
            categoryId: question.categoryId || quiz.categoryId,
            // Ensure options is always an array
            options: Array.isArray(question.options)
              ? question.options
              : typeof question.options === "string"
              ? JSON.parse(question.options)
              : ["", "", "", ""],
            // Ensure correctAnswer is a number
            correctAnswer:
              typeof question.correctAnswer === "number"
                ? question.correctAnswer
                : 0,
          };
        });

        console.log("Formatted questions:", formattedQuestions);
        setQuizQuestions(formattedQuestions);
      } else {
        console.error("Failed to fetch quiz questions:", response.message);
        setQuizQuestions([]);
        toast.error("Failed to fetch quiz questions");
      }
    } catch (error) {
      console.error("Error fetching quiz questions:", error);
      setQuizQuestions([]);
      toast.error("Error fetching quiz questions");
    } finally {
      setLoading(false);
    }

    // Also fetch available practice questions for this category
    fetchAvailablePracticeQuestions(quiz.categoryId);
  };

  // Fetch available practice questions for the selected quiz category
  const fetchAvailablePracticeQuestions = async (categoryId) => {
    setLoadingPracticeQuestions(true);
    try {
      // Assuming your API has a method to get practice questions by category
      const response = await api.practiceQuestions.getByCategoryId(categoryId);
      if (response.success) {
        // Filter out questions that are already in the quiz
        const alreadyAddedIds = quizQuestions.map(q => q.id);
        const availableQuestions = response.data.filter(
          q => !alreadyAddedIds.includes(q.id)
        );
        
        setAvailablePracticeQuestions(availableQuestions);
      } else {
        console.error("Failed to fetch practice questions:", response.message);
        setAvailablePracticeQuestions([]);
        toast.error("Failed to fetch available practice questions");
      }
    } catch (error) {
      console.error("Error fetching practice questions:", error);
      setAvailablePracticeQuestions([]);
      toast.error("Error fetching available practice questions");
    } finally {
      setLoadingPracticeQuestions(false);
    }
  };

  // Toggle selected practice question
  const togglePracticeQuestionSelection = (question) => {
    if (selectedPracticeQuestions.some(q => q.id === question.id)) {
      setSelectedPracticeQuestions(selectedPracticeQuestions.filter(q => q.id !== question.id));
    } else {
      setSelectedPracticeQuestions([...selectedPracticeQuestions, question]);
    }
  };

  // Add selected practice questions to the quiz
  const addSelectedPracticeQuestionsToQuiz = async () => {
    if (selectedPracticeQuestions.length === 0) {
      toast.warning("Please select at least one question to add");
      return;
    }

    try {
      // Format the questions for the API request - just need the IDs
      const questionIds = selectedPracticeQuestions.map(q => q.id);
      
      // Assuming your API has a method to add existing practice questions to a quiz
      const response = await api.quizzes.addExistingQuestionsToQuiz(activeQuiz.id, questionIds);
      
      if (response.success) {
        // Refresh the quiz questions
        const refreshResponse = await api.quizzes.getQuizQuestions(activeQuiz.id);
        if (refreshResponse.success) {
          // Process the response data structure - use the same formatting logic as in manageQuestionsInline
          const formattedQuestions = refreshResponse.data.map((item) => {
            // Handle both direct question objects and nested question objects
            const question = item.question || item;
            return {
              ...question,
              id: question.id,
              // Ensure categoryId is present
              categoryId: question.categoryId || activeQuiz.categoryId,
              // Ensure options is always an array
              options: Array.isArray(question.options)
                ? question.options
                : typeof question.options === "string"
                ? JSON.parse(question.options)
                : ["", "", "", ""],
              // Ensure correctAnswer is a number
              correctAnswer:
                typeof question.correctAnswer === "number"
                  ? question.correctAnswer
                  : 0,
            };
          });
          
          setQuizQuestions(formattedQuestions);
          toast.success(`${selectedPracticeQuestions.length} questions added to quiz successfully`);
          
          // Clear selection and refresh available questions
          setSelectedPracticeQuestions([]);
          fetchAvailablePracticeQuestions(activeQuiz.categoryId);
          setShowAvailableQuestions(false);
        }
      } else {
        toast.error("Failed to add questions to quiz: " + response.message);
      }
    } catch (error) {
      console.error("Error adding questions to quiz:", error);
      toast.error("Error adding questions to quiz");
    }
  };

  // Filter available practice questions based on search term
  const filteredPracticeQuestions = practiceQuestionSearchTerm.trim() === "" 
    ? availablePracticeQuestions 
    : availablePracticeQuestions.filter(q => 
        q.questionText.toLowerCase().includes(practiceQuestionSearchTerm.toLowerCase()));

  // Close question management panel
  const closeQuestionManagement = () => {
    setActiveQuiz(null);
    setQuizQuestions([]);
  };

  // Add new question to quiz
  const addQuestion = () => {
    setEditQuestionIndex(null);
    setNewQuestion({
      id: null,
      questionText: "",
      options: ["", "", "", ""],
      correctAnswer: 0,
      explanation: "",
      categoryId: activeQuiz.categoryId,
      isActive: true,
    });
    setShowQuestionModal(true);
  };

  // Edit existing question
  const editQuestion = (index) => {
    setEditQuestionIndex(index);
    setNewQuestion({ ...quizQuestions[index] });
    setShowQuestionModal(true);
  };

  // Delete question
  const deleteQuestion = async (quizId, questionId) => {
    if (window.confirm("Are you sure you want to remove this question from the quiz? The question will still be available in the practice questions database.")) {
      try {
        const response = await api.quizzes.deleteQuestion(quizId, questionId);
        if (response.success) {
          setQuizQuestions(quizQuestions.filter((q) => q.id !== questionId));
          
          // Also update the availablePracticeQuestions state to include the question that was just removed
          // First get the removed question
          const removedQuestion = quizQuestions.find(q => q.id === questionId);
          if (removedQuestion) {
            // Check if the question is already in the available questions array to prevent duplicates
            const questionAlreadyAvailable = availablePracticeQuestions.some(q => q.id === questionId);
            
            if (!questionAlreadyAvailable) {
              setAvailablePracticeQuestions([...availablePracticeQuestions, removedQuestion]);
            }
          }
          
          toast.success("Question removed from quiz successfully");
        } else {
          toast.error("Failed to remove question from quiz: " + response.message);
        }
      } catch (error) {
        console.error("Error removing question from quiz:", error);
        toast.error("Error removing question from quiz");
      }
    }
  };

  // Handle question form changes
  const handleQuestionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewQuestion({
      ...newQuestion,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle option changes
  const handleOptionChange = (index, value) => {
    const updatedOptions = [...newQuestion.options];
    updatedOptions[index] = value;
    setNewQuestion({
      ...newQuestion,
      options: updatedOptions,
    });
  };

  // Save question
  const saveQuestion = async () => {
    // Validation
    if (!newQuestion.questionText.trim()) {
      toast.error("Question text is required");
      return;
    }

    if (newQuestion.options.some((opt) => !opt.trim())) {
      toast.error("All options must have text");
      return;
    }

    // Ensure categoryId is set
    if (!newQuestion.categoryId) {
      setNewQuestion({
        ...newQuestion,
        categoryId: activeQuiz.categoryId,
      });
    }

    try {
      let response;
      if (editQuestionIndex !== null) {
        // Update existing question
        response = await api.quizzes.updateQuestion(
          activeQuiz.id,
          newQuestion.id,
          newQuestion
        );
        if (response.success) {
          const updatedQuestions = [...quizQuestions];
          updatedQuestions[editQuestionIndex] = response.data;
          setQuizQuestions(updatedQuestions);
          toast.success("Question updated successfully");
        } else {
          toast.error("Failed to update question: " + response.message);
        }
      } else {
        // Add new question
        response = await api.quizzes.createQuestion(activeQuiz.id, newQuestion);
        if (response.success) {
          setQuizQuestions([...quizQuestions, response.data]);
          toast.success("Question added successfully");
        } else {
          toast.error("Failed to add question: " + response.message);
        }
      }
      setShowQuestionModal(false);
    } catch (error) {
      console.error("Error saving question:", error);
      toast.error("Error saving question");
    }
  };

  // Download sample CSV
  const downloadSampleCsv = () => {
    const sampleContent = [
      "What is the proper way to store hazardous materials?,In any available container,In properly labeled sealed containers in designated areas,Near heat sources,Mixed with other chemicals,1,Environmental Care",
      "What should be your first action if you witness a workplace injury?,Call emergency services,Notify your supervisor,Ensure the area is safe and provide first aid if qualified,Document the incident,2,Health and Safety Measures",
      "When working at heights what is a key safety measure?,Working alone to minimize distractions,Using improvised tools to save time,Inspecting equipment before use and using proper fall protection,Working during adverse weather conditions,2,Safe Working Practices",
      "What are the main requirements of health and safety legislation?,To reduce workplace accidents,To provide financial compensation,To ensure that employers and employees follow safety regulations,To increase productivity,2,Laws and Regulations",
      "What should be included in a risk assessment?,Only major hazards,All potential hazards and who might be harmed,Previous accident reports only,Just control measures,1,Hazards and Risks",
    ].join("\n");

    const blob = new Blob([sampleContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_questions.csv");
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Sample CSV downloaded successfully");
  };

  // Import questions from CSV
  const importCSV = async (e) => {
    const file = e.target.files[0];
    setCsvError(null);

    if (!file) {
      return;
    }

    // Check file extension
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv") {
      setCsvError("Please upload a CSV file.");
      toast.error("Please upload a CSV file.");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const csvData = event.target.result;
        const lines = csvData.split("\n");

        // Filter out empty lines
        const nonEmptyLines = lines.filter((line) => line.trim() !== "");

        // Parse CSV lines
        const parsedQuestions = nonEmptyLines.map((line) => {
          const values = line.split(",").map((value) => value.trim());

          // Check if there are enough values
          if (values.length < 7) {
            throw new Error(
              `Line does not have enough values. Format: question,option1,option2,option3,option4,correctOptionIndex,category`
            );
          }

          const [
            questionText,
            option1,
            option2,
            option3,
            option4,
            correctAnswerStr,
            categoryName,
            subcategoryName = "",
            explanation = "",
          ] = values;
          const correctAnswer = parseInt(correctAnswerStr, 10);

          // Validate correctAnswer
          if (isNaN(correctAnswer) || correctAnswer < 0 || correctAnswer > 3) {
            throw new Error(
              `Invalid correctOptionIndex. It should be a number between 0 and 3.`
            );
          }

          return {
            questionText,
            options: [option1, option2, option3, option4],
            correctAnswer,
            explanation,
            categoryId: activeQuiz.categoryId,
            isActive: true,
          };
        });

        // Bulk import questions
        try {
          const response = await api.quizzes.bulkImportQuestions(
            activeQuiz.id,
            parsedQuestions
          );
          if (response.success) {
            // Refresh quiz questions
            const questionsResponse = await api.quizzes.getQuizQuestions(
              activeQuiz.id
            );
            if (questionsResponse.success) {
              setQuizQuestions(questionsResponse.data);
              toast.success(
                `${parsedQuestions.length} questions imported successfully.`
              );
            }
          } else {
            toast.error("Failed to import questions: " + response.message);
          }
        } catch (error) {
          console.error("Error importing questions:", error);
          toast.error("Error importing questions");
        }

        // Reset the file input
        e.target.value = null;
      } catch (error) {
        setCsvError(error.message);
        toast.error("Error parsing CSV: " + error.message);
        e.target.value = null;
      }
    };

    reader.onerror = () => {
      setCsvError("Failed to read the file. Please try again.");
      toast.error("Failed to read the file. Please try again.");
      e.target.value = null;
    };

    reader.readAsText(file);
  };

  // Get category name by ID
  const getCategoryNameById = (categoryId) => {
    return (
      categories.find((category) => category.id === categoryId)?.name ||
      "Unknown"
    );
  };

  return (
    <div className="container-fluid">
      {/* Quiz List */}
      {activeQuiz === null ? (
        <>
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header border-bottom bg-base py-16 px-24">
                  <h6 className="text-lg fw-semibold mb-0">Manage Quiz MCQs</h6>
                </div>
                <div className="card-body p-24">
                  {/* Filters Section */}
                  <div className="row mb-4">
                    <div className="col-lg-3 mb-3">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Filter by Category
                      </label>
                      <select
                        className="form-select"
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                      >
                        <option value="All">All Categories</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.name}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg-3 mb-3">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Filter by Question Subcategory
                      </label>
                      <select
                        className="form-select"
                        value={selectedSubcategory}
                        onChange={handleSubcategoryChange}
                        disabled={selectedCategory === "All"}
                      >
                        <option value="All">All Subcategories</option>
                        {subcategories.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.name}>
                            {subcategory.name}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">
                        Filters quizzes with questions in this subcategory
                      </small>
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                        Search Quizzes
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by title or description"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setSearchTerm("")}
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h6 className="mb-0">
                        Available Quizzes: {filteredQuizzes.length}
                      </h6>
                    </div>
                    <div>
                      <button className="btn btn-primary" onClick={addQuiz}>
                        <Icon icon="mdi:plus" className="me-1" />
                        Create New Quiz
                      </button>
                    </div>
                  </div>

                  {/* Loading state */}
                  {loading && (
                    <div className="d-flex justify-content-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}

                  {/* Error state */}
                  {error && (
                    <div className="alert alert-danger">
                      {error}. Please try again or contact support.
                    </div>
                  )}

                  {/* Quizzes Table */}
                  {!loading && !error && filteredQuizzes.length === 0 ? (
                    <div className="alert alert-info">
                      No quizzes found matching your criteria.
                    </div>
                  ) : (
                    !loading &&
                    !error && (
                      <div className="table-responsive">
                        <table className="table table-hover table-bordered">
                          <thead className="bg-light">
                            <tr>
                              <th>#</th>
                              <th>Title</th>
                              <th>Category</th>
                              <th>Questions</th>
                              <th>Time (min)</th>
                              <th>Passing Score</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredQuizzes.map((quiz, index) => (
                              <tr
                                key={quiz.id}
                                className={
                                  !quiz.isActive ? "table-secondary" : ""
                                }
                              >
                                <td>{index + 1}</td>
                                <td>
                                  <div className="fw-bold">{quiz.title}</div>
                                  <div className="small text-muted">
                                    {quiz.description}
                                  </div>
                                  <div className="small text-muted">
                                    Created:{" "}
                                    {new Date(
                                      quiz.dateCreated
                                    ).toLocaleDateString()}
                                  </div>
                                </td>
                                <td>
                                  <span className="badge bg-primary d-block mb-1">
                                    {getCategoryNameById(quiz.categoryId)}
                                  </span>
                                </td>
                                <td className="text-center">
                                  {quiz.questions ? quiz.questions.length : 0}
                                </td>
                                <td className="text-center">
                                  {quiz.timeLimit}
                                </td>
                                <td className="text-center">
                                  {quiz.passingScore}%
                                </td>
                                <td>
                                  <div className="form-check form-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={quiz.isActive}
                                      onChange={() =>
                                        toggleQuizStatus(quiz.id, quiz.isActive)
                                      }
                                      id={`status-${quiz.id}`}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`status-${quiz.id}`}
                                    >
                                      {quiz.isActive ? "Active" : "Inactive"}
                                    </label>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex gap-1 flex-wrap">
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() =>
                                        manageQuestionsInline(quiz)
                                      }
                                      title="Manage Questions Directly"
                                    >
                                      <Icon icon="mdi:format-list-bulleted" />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-info"
                                      onClick={() => editQuiz(index)}
                                      title="Edit Quiz"
                                    >
                                      <Icon icon="mdi:pencil" />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => duplicateQuiz(quiz)}
                                      title="Duplicate Quiz"
                                    >
                                      <Icon icon="mdi:content-duplicate" />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() => deleteQuiz(quiz.id)}
                                      title="Delete Quiz"
                                    >
                                      <Icon icon="mdi:delete" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Question Management Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center">
                  <div>
                    <button
                      className="btn btn-sm btn-outline-secondary mb-2"
                      onClick={closeQuestionManagement}
                    >
                      <Icon icon="mdi:arrow-left" className="me-1" />
                      Back to Quizzes
                    </button>
                    <h6 className="text-lg fw-semibold mb-0">
                      {activeQuiz.title} - Questions
                    </h6>
                    <p className="mb-0">{activeQuiz.description}</p>
                  </div>
                  <div className="badge bg-primary fs-6">
                    {quizQuestions.length} Questions
                  </div>
                </div>
                <div className="card-body p-24">
                  {/* Search and Actions */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search questions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setSearchTerm("")}
                          >
                            <Icon icon="mdi:close" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          className="btn btn-outline-primary"
                          onClick={addQuestion}
                        >
                          <Icon icon="mdi:plus" className="me-1" />
                          Add Question
                        </button>
                        <button
                          className="btn btn-outline-success"
                          onClick={() => setShowAvailableQuestions(!showAvailableQuestions)}
                        >
                          <Icon icon="mdi:database-search" className="me-1" />
                          {showAvailableQuestions ? "Hide Available Questions" : "Browse Available Questions"}
                        </button>
                        <div className="dropdown">
                          <button
                            className="btn btn-outline-secondary dropdown-toggle"
                            type="button"
                            data-bs-toggle="dropdown"
                          >
                            <Icon
                              icon="mdi:file-import-outline"
                              className="me-1"
                            />
                            Import / Export
                          </button>
                          <ul className="dropdown-menu">
                            <li>
                              <button
                                className="dropdown-item"
                                type="button"
                                onClick={() => csvFileInputRef.current.click()}
                              >
                                <Icon
                                  icon="mdi:file-upload"
                                  className="me-2 text-primary"
                                />
                                Import CSV
                              </button>
                              <input
                                type="file"
                                accept=".csv"
                                onChange={importCSV}
                                style={{ display: "none" }}
                                ref={csvFileInputRef}
                              />
                            </li>
                            <li>
                              <button
                                className="dropdown-item"
                                type="button"
                                onClick={downloadSampleCsv}
                              >
                                <Icon
                                  icon="mdi:file-download"
                                  className="me-2 text-success"
                                />
                                Download Sample CSV
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Display CSV error if any */}
                  {csvError && (
                    <div className="alert alert-danger mb-3">
                      <h6 className="mb-1">Error importing CSV</h6>
                      <p className="mb-0">{csvError}</p>
                    </div>
                  )}

                  {/* Available Practice Questions Section */}
                  {showAvailableQuestions && (
                    <div className="card mb-4 border-success">
                      <div className="card-header bg-success-subtle d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 text-success">Available Practice Questions ({filteredPracticeQuestions.length})</h6>
                        <div className="d-flex gap-2">
                          <div className="input-group input-group-sm" style={{ width: '300px' }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search questions..."
                              value={practiceQuestionSearchTerm}
                              onChange={(e) => setPracticeQuestionSearchTerm(e.target.value)}
                            />
                            {practiceQuestionSearchTerm && (
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => setPracticeQuestionSearchTerm("")}
                              >
                                <Icon icon="mdi:close" />
                              </button>
                            )}
                          </div>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={addSelectedPracticeQuestionsToQuiz}
                            disabled={selectedPracticeQuestions.length === 0}
                          >
                            Add Selected ({selectedPracticeQuestions.length})
                          </button>
                        </div>
                      </div>
                      <div className="card-body p-3">
                        {loadingPracticeQuestions ? (
                          <div className="d-flex justify-content-center py-3">
                            <div className="spinner-border text-success" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : filteredPracticeQuestions.length === 0 ? (
                          <div className="alert alert-info mb-0">
                            No available practice questions found for this category. All questions may already be added to this quiz.
                          </div>
                        ) : (
                          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table table-hover table-sm">
                              <thead className="table-light sticky-top">
                                <tr>
                                  <th style={{ width: '50px' }}>
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={
                                          selectedPracticeQuestions.length === filteredPracticeQuestions.length &&
                                          filteredPracticeQuestions.length > 0
                                        }
                                        onChange={() => {
                                          if (selectedPracticeQuestions.length === filteredPracticeQuestions.length) {
                                            setSelectedPracticeQuestions([]);
                                          } else {
                                            setSelectedPracticeQuestions([...filteredPracticeQuestions]);
                                          }
                                        }}
                                      />
                                    </div>
                                  </th>
                                  <th>Question</th>
                                  <th>Options</th>
                                  <th style={{ width: '100px' }}>Answer</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredPracticeQuestions.map((question) => (
                                  <tr
                                    key={question.id}
                                    className={
                                      selectedPracticeQuestions.some(q => q.id === question.id)
                                        ? 'table-success'
                                        : ''
                                    }
                                    onClick={() => togglePracticeQuestionSelection(question)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <td>
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={selectedPracticeQuestions.some(q => q.id === question.id)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            togglePracticeQuestionSelection(question);
                                          }}
                                        />
                                      </div>
                                    </td>
                                    <td>
                                      <div className="text-truncate" style={{ maxWidth: '250px' }}>
                                        {question.questionText}
                                      </div>
                                      {question.explanation && (
                                        <div
                                          className="small text-muted text-truncate"
                                          style={{ maxWidth: '250px' }}
                                        >
                                          <strong>Explanation:</strong> {question.explanation}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <ol className="ps-3 mb-0 small">
                                        {(Array.isArray(question.options) ? question.options : []).map(
                                          (option, idx) => (
                                            <li
                                              key={idx}
                                              className={
                                                idx === question.correctAnswer
                                                  ? 'text-success fw-bold'
                                                  : ''
                                              }
                                            >
                                              <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                                {option}
                                              </div>
                                            </li>
                                          )
                                        )}
                                      </ol>
                                    </td>
                                    <td className="text-center">
                                      Option {question.correctAnswer + 1}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Loading state */}
                  {loading && (
                    <div className="d-flex justify-content-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}

                  {/* Questions Table */}
                  {!loading && quizQuestions.length === 0 ? (
                    <div className="alert alert-info">
                      No questions added yet. Click "Add Question" to create
                      your first question or import questions from a CSV file.
                    </div>
                  ) : (
                    !loading && (
                      <div className="table-responsive">
                        <table className="table table-hover table-bordered">
                          <thead className="bg-light">
                            <tr>
                              <th style={{ width: "5%" }}>#</th>
                              <th style={{ width: "50%" }}>Question</th>
                              <th style={{ width: "25%" }}>Options</th>
                              <th style={{ width: "10%" }}>Answer</th>
                              <th style={{ width: "10%" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quizQuestions.map((question, index) => (
                              <tr key={question.id}>
                                <td>{index + 1}</td>
                                <td>
                                  <div>{question.questionText}</div>
                                  {question.explanation && (
                                    <div className="small text-muted mt-1">
                                      <strong>Explanation:</strong>{" "}
                                      {question.explanation}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <ol className="ps-3 mb-0">
                                    {question.options.map((option, idx) => (
                                      <li
                                        key={idx}
                                        className={
                                          idx === question.correctAnswer
                                            ? "text-success fw-bold"
                                            : ""
                                        }
                                      >
                                        {option}
                                      </li>
                                    ))}
                                  </ol>
                                </td>
                                <td className="text-center">
                                  Option {question.correctAnswer + 1}
                                </td>
                                <td>
                                  <div className="d-flex gap-1 flex-wrap">
                                    <button
                                      className="btn btn-sm btn-info"
                                      onClick={() => editQuestion(index)}
                                      title="Edit Question"
                                    >
                                      <Icon icon="mdi:pencil" />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() =>
                                        deleteQuestion(
                                          activeQuiz.id,
                                          question.id
                                        )
                                      }
                                      title="Delete Question"
                                    >
                                      <Icon icon="mdi:delete" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add/Edit Quiz Modal */}
      {showAddModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editQuizIndex !== null ? "Edit Quiz" : "Create New Quiz"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold">Quiz Title</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={newQuiz.title}
                      onChange={handleQuizChange}
                      placeholder="Enter quiz title"
                      required
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold">
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={newQuiz.description}
                      onChange={handleQuizChange}
                      rows="2"
                      placeholder="Enter quiz description"
                    ></textarea>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Category</label>
                    <select
                      className="form-select"
                      name="categoryId"
                      value={newQuiz.categoryId}
                      onChange={handleQuizChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <small className="text-muted">
                      Note: Questions will be selected based on the category. This allows
                      students to practice with relevant material for their studies.
                    </small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">
                      Number of Questions
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="questionCount"
                      value={newQuiz.questionCount}
                      onChange={handleQuizChange}
                      min="1"
                      required
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">
                      Time Limit (minutes)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="timeLimit"
                      value={newQuiz.timeLimit}
                      onChange={handleQuizChange}
                      min="1"
                      required
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">
                      Passing Score (%)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="passingScore"
                      value={newQuiz.passingScore}
                      onChange={handleQuizChange}
                      min="1"
                      max="100"
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="isActive"
                      id="quizActiveStatus"
                      checked={newQuiz.isActive}
                      onChange={handleQuizChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="quizActiveStatus"
                    >
                      Quiz is active and available
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveQuiz}
                >
                  {editQuizIndex !== null ? "Update Quiz" : "Create Quiz"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Question Modal */}
      {showQuestionModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editQuestionIndex !== null
                    ? "Edit Question"
                    : "Add New Question"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowQuestionModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Question Text
                  </label>
                  <textarea
                    className="form-control"
                    name="questionText"
                    value={newQuestion.questionText}
                    onChange={handleQuestionChange}
                    rows="2"
                    placeholder="Enter your question here"
                    required
                  ></textarea>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Options (Select the correct answer)
                  </label>
                  {newQuestion.options.map((option, index) => (
                    <div key={index} className="mb-2 d-flex">
                      <div
                        className="form-check me-2"
                        style={{
                          minWidth: "45px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          id={`option-${index}`}
                          type="radio"
                          className="form-check-input"
                          name="correctAnswer"
                          value={index}
                          checked={Number(newQuestion.correctAnswer) === index}
                          onChange={(e) =>
                            setNewQuestion({
                              ...newQuestion,
                              correctAnswer: Number(e.target.value),
                            })
                          }
                        />
                        <label
                          className="form-check-label ms-1"
                          htmlFor={`option-${index}`}
                        >
                          {index + 1}
                        </label>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        value={option}
                        onChange={(e) =>
                          handleOptionChange(index, e.target.value)
                        }
                        placeholder={`Enter option ${index + 1}`}
                        required
                      />
                    </div>
                  ))}
                  <div className="alert alert-info mt-2">
                    <Icon icon="mdi:information" className="me-1" />
                    Select the radio button next to the correct answer option.
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Explanation</label>
                  <textarea
                    className="form-control"
                    name="explanation"
                    value={newQuestion.explanation}
                    onChange={handleQuestionChange}
                    rows="2"
                    placeholder="Explain why the correct answer is right"
                  ></textarea>
                </div>

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isActive"
                    name="isActive"
                    checked={newQuestion.isActive}
                    onChange={handleQuestionChange}
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Question is active and available in the quiz
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowQuestionModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveQuestion}
                >
                  {editQuestionIndex !== null
                    ? "Update Question"
                    : "Add Question"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizMCQsLayer;
